import { useMemo, useState } from 'react'
import { useDecks } from '../hooks/useDecks'
import { useFolders } from '../hooks/useFolders'
import { useAddDeckToCourse, useAddFolderToCourse, useCourse } from '../hooks/useCourses'

type Tab = 'decks' | 'folders'

interface Props {
  courseId: string
  onClose: () => void
}

export default function AddToCourseModal({ courseId, onClose }: Props) {
  const [tab, setTab] = useState<Tab>('decks')
  const [search, setSearch] = useState('')
  // Tracks items added during this modal session (for "Added ✓" affordance)
  const [addedDeckIds, setAddedDeckIds] = useState<Set<string>>(new Set())
  const [addedFolderIds, setAddedFolderIds] = useState<Set<string>>(new Set())
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set())
  // Per-row errors keyed by item id
  const [rowErrors, setRowErrors] = useState<Map<string, string>>(new Map())

  const { data: course } = useCourse(courseId)
  const { data: decksData } = useDecks(1, 100, null, search || undefined)
  const { data: foldersData } = useFolders()
  const addDeck = useAddDeckToCourse()
  const addFolder = useAddFolderToCourse()

  // Deck IDs already in the course when the modal opened (standalone + via folder)
  const existingDeckIds = useMemo(() => {
    if (!course) return new Set<string>()
    const ids = new Set<string>(course.standalone_decks.map((d) => d.id))
    course.folders.forEach((f) => f.decks.forEach((d) => ids.add(d.id)))
    return ids
  }, [course])

  // Folder IDs already in the course when the modal opened
  const existingFolderIds = useMemo(() => {
    if (!course) return new Set<string>()
    return new Set<string>(course.folders.map((f) => f.id))
  }, [course])

  const allDecks = decksData?.items ?? []
  const allFolders = foldersData?.items ?? []

  const decks = allDecks.filter(
    (d) => !search || d.title.toLowerCase().includes(search.toLowerCase()),
  )
  const folders = allFolders.filter(
    (f) => !search || f.title.toLowerCase().includes(search.toLowerCase()),
  )

  const totalAdded = addedDeckIds.size + addedFolderIds.size

  function clearRowError(id: string) {
    setRowErrors((prev) => { const next = new Map(prev); next.delete(id); return next })
  }

  function handleAddDeck(deckId: string) {
    if (existingDeckIds.has(deckId) || addedDeckIds.has(deckId) || pendingIds.has(deckId)) return
    clearRowError(deckId)
    setPendingIds((prev) => new Set(prev).add(deckId))
    addDeck.mutate({ courseId, deckId }, {
      onSuccess: () => {
        setAddedDeckIds((prev) => new Set(prev).add(deckId))
        setPendingIds((prev) => { const next = new Set(prev); next.delete(deckId); return next })
      },
      onError: () => {
        setPendingIds((prev) => { const next = new Set(prev); next.delete(deckId); return next })
        setRowErrors((prev) => new Map(prev).set(deckId, 'Could not add — tap to retry'))
      },
    })
  }

  function handleAddFolder(folderId: string) {
    if (existingFolderIds.has(folderId) || addedFolderIds.has(folderId) || pendingIds.has(folderId)) return
    clearRowError(folderId)
    setPendingIds((prev) => new Set(prev).add(folderId))
    addFolder.mutate({ courseId, folderId }, {
      onSuccess: () => {
        setAddedFolderIds((prev) => new Set(prev).add(folderId))
        setPendingIds((prev) => { const next = new Set(prev); next.delete(folderId); return next })
      },
      onError: () => {
        setPendingIds((prev) => { const next = new Set(prev); next.delete(folderId); return next })
        setRowErrors((prev) => new Map(prev).set(folderId, 'Could not add — tap to retry'))
      },
    })
  }

  // Empty-state helpers
  function deckEmptyMessage() {
    if (decks.length === 0 && search) return 'No decks match your search'
    if (decks.length === 0) return "You don't have any decks yet"
    const addable = decks.filter((d) => !existingDeckIds.has(d.id) && !addedDeckIds.has(d.id))
    if (addable.length === 0 && search) return 'All matching decks are already in this course'
    if (addable.length === 0) return 'All your decks are already in this course'
    return null
  }

  function folderEmptyMessage() {
    if (folders.length === 0 && search) return 'No folders match your search'
    if (folders.length === 0) return "You don't have any folders yet"
    const addable = folders.filter((f) => !existingFolderIds.has(f.id) && !addedFolderIds.has(f.id))
    if (addable.length === 0 && search) return 'All matching folders are already in this course'
    if (addable.length === 0) return 'All your folders are already in this course'
    return null
  }

  function DeckBadge({ deck }: { deck: { id: string; title: string } }) {
    const isExisting = existingDeckIds.has(deck.id)
    const isAdded = addedDeckIds.has(deck.id)
    const isPending = pendingIds.has(deck.id)
    const rowError = rowErrors.get(deck.id)

    if (isExisting) {
      return (
        <span style={{ padding: '6px 12px', background: 'var(--bg-tertiary, var(--bg-secondary))', color: 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)', fontSize: 12, fontWeight: 600, flexShrink: 0, whiteSpace: 'nowrap' }}>
          In course
        </span>
      )
    }
    if (isAdded) {
      return (
        <span style={{ padding: '6px 12px', background: 'color-mix(in srgb, var(--accent) 12%, transparent)', color: 'var(--accent)', border: '1px solid var(--accent)', borderRadius: 'var(--radius-pill)', fontSize: 12, fontWeight: 600, flexShrink: 0, whiteSpace: 'nowrap' }}>
          Added ✓
        </span>
      )
    }
    if (rowError) {
      return (
        <button
          onClick={() => handleAddDeck(deck.id)}
          title={rowError}
          style={{ padding: '6px 14px', background: 'color-mix(in srgb, var(--danger) 10%, transparent)', color: 'var(--danger)', border: '1px solid var(--danger)', borderRadius: 'var(--radius-pill)', fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}
        >
          Retry
        </button>
      )
    }
    return (
      <button
        onClick={() => handleAddDeck(deck.id)}
        disabled={isPending}
        style={{ padding: '6px 14px', background: 'var(--accent-fill)', color: 'var(--text-on-accent)', border: 'none', borderRadius: 'var(--radius-pill)', fontSize: 12, fontWeight: 600, cursor: isPending ? 'wait' : 'pointer', flexShrink: 0, opacity: isPending ? 0.6 : 1, minWidth: 52 }}
      >
        {isPending ? '…' : 'Add'}
      </button>
    )
  }

  function FolderBadge({ folder }: { folder: { id: string; title: string } }) {
    const isExisting = existingFolderIds.has(folder.id)
    const isAdded = addedFolderIds.has(folder.id)
    const isPending = pendingIds.has(folder.id)
    const rowError = rowErrors.get(folder.id)

    if (isExisting) {
      return (
        <span style={{ padding: '6px 12px', background: 'var(--bg-tertiary, var(--bg-secondary))', color: 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)', fontSize: 12, fontWeight: 600, flexShrink: 0, whiteSpace: 'nowrap' }}>
          In course
        </span>
      )
    }
    if (isAdded) {
      return (
        <span style={{ padding: '6px 12px', background: 'color-mix(in srgb, var(--accent) 12%, transparent)', color: 'var(--accent)', border: '1px solid var(--accent)', borderRadius: 'var(--radius-pill)', fontSize: 12, fontWeight: 600, flexShrink: 0, whiteSpace: 'nowrap' }}>
          Added ✓
        </span>
      )
    }
    if (rowError) {
      return (
        <button
          onClick={() => handleAddFolder(folder.id)}
          title={rowError}
          style={{ padding: '6px 14px', background: 'color-mix(in srgb, var(--danger) 10%, transparent)', color: 'var(--danger)', border: '1px solid var(--danger)', borderRadius: 'var(--radius-pill)', fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}
        >
          Retry
        </button>
      )
    }
    return (
      <button
        onClick={() => handleAddFolder(folder.id)}
        disabled={isPending}
        style={{ padding: '6px 14px', background: 'var(--accent-fill)', color: 'var(--text-on-accent)', border: 'none', borderRadius: 'var(--radius-pill)', fontSize: 12, fontWeight: 600, cursor: isPending ? 'wait' : 'pointer', flexShrink: 0, opacity: isPending ? 0.6 : 1, minWidth: 52 }}
      >
        {isPending ? '…' : 'Add'}
      </button>
    )
  }

  const deckEmpty = deckEmptyMessage()
  const folderEmpty = folderEmptyMessage()

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 50, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{ background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-float)', width: '100%', maxWidth: 480, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{ padding: '20px 24px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>Add to Course</h2>
            {totalAdded > 0 && (
              <p style={{ margin: '3px 0 0', fontSize: 13, color: 'var(--accent)' }}>
                {totalAdded} item{totalAdded !== 1 ? 's' : ''} added
              </p>
            )}
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 22, padding: 4, lineHeight: 1 }}>×</button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 0, padding: '12px 24px 0', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          {(['decks', 'folders'] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => { setTab(t); setSearch('') }}
              style={{
                padding: '8px 16px', background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 14, fontWeight: 600, color: tab === t ? 'var(--accent)' : 'var(--text-secondary)',
                borderBottom: tab === t ? '2px solid var(--accent)' : '2px solid transparent',
                marginBottom: -1, textTransform: 'capitalize',
              }}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Search */}
        <div style={{ padding: '12px 24px', flexShrink: 0 }}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Search ${tab}…`}
            style={{ width: '100%', padding: '8px 12px', fontSize: 14, background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box' }}
            onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--accent)')}
            onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
          />
        </div>

        {/* List */}
        <div style={{ overflowY: 'auto', padding: '0 24px', display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
          {tab === 'decks' && (
            deckEmpty ? (
              <p style={{ color: 'var(--text-secondary)', fontSize: 14, textAlign: 'center', margin: '24px 0' }}>{deckEmpty}</p>
            ) : (
              decks.map((deck) => {
                const isExisting = existingDeckIds.has(deck.id)
                const isAdded = addedDeckIds.has(deck.id)
                const rowError = rowErrors.get(deck.id)
                const settled = isExisting || isAdded
                return (
                  <div
                    key={deck.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                      background: settled ? 'color-mix(in srgb, var(--accent) 6%, var(--bg-secondary))' : rowError ? 'color-mix(in srgb, var(--danger) 6%, var(--bg-secondary))' : 'var(--bg-secondary)',
                      borderRadius: 'var(--radius-sm)',
                      border: `1px solid ${settled ? 'var(--accent)' : rowError ? 'var(--danger)' : 'var(--border)'}`,
                      transition: 'border-color 0.15s, background 0.15s',
                    }}
                  >
                    <span style={{ fontSize: 16 }}>🃏</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{deck.title}</div>
                      <div style={{ fontSize: 12, color: rowError ? 'var(--danger)' : 'var(--text-secondary)' }}>
                        {rowError ?? `${deck.card_count} cards${deck.folder_title ? ` · 📁 ${deck.folder_title}` : ''}`}
                      </div>
                    </div>
                    <DeckBadge deck={deck} />
                  </div>
                )
              })
            )
          )}

          {tab === 'folders' && (
            folderEmpty ? (
              <p style={{ color: 'var(--text-secondary)', fontSize: 14, textAlign: 'center', margin: '24px 0' }}>{folderEmpty}</p>
            ) : (
              folders.map((folder) => {
                const isExisting = existingFolderIds.has(folder.id)
                const isAdded = addedFolderIds.has(folder.id)
                const rowError = rowErrors.get(folder.id)
                const settled = isExisting || isAdded
                return (
                  <div
                    key={folder.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                      background: settled ? 'color-mix(in srgb, var(--accent) 6%, var(--bg-secondary))' : rowError ? 'color-mix(in srgb, var(--danger) 6%, var(--bg-secondary))' : 'var(--bg-secondary)',
                      borderRadius: 'var(--radius-sm)',
                      border: `1px solid ${settled ? 'var(--accent)' : rowError ? 'var(--danger)' : 'var(--border)'}`,
                      transition: 'border-color 0.15s, background 0.15s',
                    }}
                  >
                    <span style={{ fontSize: 16 }}>📁</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}>{folder.title}</div>
                      <div style={{ fontSize: 12, color: rowError ? 'var(--danger)' : 'var(--text-secondary)' }}>
                        {rowError ?? `${folder.deck_count} decks`}
                      </div>
                    </div>
                    <FolderBadge folder={folder} />
                  </div>
                )
              })
            )
          )}

          {/* Spacer so last item isn't flush against the Done button */}
          <div style={{ height: 8 }} />
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 24px 20px', flexShrink: 0, borderTop: '1px solid var(--border)' }}>
          <button
            onClick={onClose}
            style={{ width: '100%', padding: '11px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
          >
            {totalAdded > 0 ? `Done (${totalAdded} added)` : 'Done'}
          </button>
        </div>
      </div>
    </div>
  )
}
